import { Table } from "antd";
import type { Key, SorterResult } from "antd/es/table/interface";
import type { ColumnsType, TableProps } from "antd/lib/table";
import i18n from "i18next";
import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router";

import { getAssignedDataGroup } from "../../../../core/router/auth";
import { BASE_PATH, FORM_ACTION } from "../../../../core/router/types/routerTypes";
import type { Entity } from "../../../../core/types/fe";
import { ActionBar } from "../../../ActionBar/ActionBar";
import { SecondaryButton } from "../../../Buttons/components/ActionButton";
import { CopyButton } from "../../../FeatureButtons/CopyButton";
import { DeleteButton } from "../../../FeatureButtons/DeleteButton";
import { NotificationType, showSuccessNotification } from "../../../NotificationAlerts";
import { NotificationAlert } from "../../../NotificationAlerts/NotificationAlert";
import type { AlertActionPayload, ExtendedAlertActionPayload } from "../../../NotificationAlerts/types";
import { type ExtendedColumnsType, useUpdateTableDataMutation } from "../../index";
import { TableStyle } from "../../styles/AntdTableStyle";
import { Actions } from "../../types/types";

export interface PropertyTableProps<T> {
    getColumnsConfig: (t: Function, sortInfo: SorterResult<T>) => ExtendedColumnsType<T>[];
    propertyAsRowKey: keyof T;
    tableData: T[];
    // Unfiltered dataset for addRow's duplicate check/append. Defaults to tableData.
    // Set only when tableData is filtered (e.g. a search bar) AND Actions.ADD is enabled;
    // customActions add buttons (e.g. AddPhysicalButton) bypass addRow and don't need it.
    allData?: T[];
    readOnly: boolean;
    onDataChanged?: Function;
    elementSelector?: React.JSX.Element;
    actions?: Actions[];
    actionCallbacks?: {
        add?: () => void;
        delete?: (item?: Entity) => void;
        copy?: (item: Entity) => void;
        formCopy?: () => void;
    };
    customActions?: React.JSX.Element[];
    onRow?: TableProps<T>["onRow"];
    rowsAreSelectable?: boolean;
    isFetching?: boolean;
    name: string;
    readOnlyVisibilityEnabled?: boolean;
    resourceUrl?: string;
    pagination?: TableProps<T>["pagination"] | false;
    addButtonName?: string;
    onSelectionChange?: (selectedRowKeys: Key[]) => void;
}

export type DataItemWithKey<T> = T & { key: T[keyof T] };

export const PropertyTable = <T extends Entity>({
    getColumnsConfig,
    propertyAsRowKey,
    tableData = [],
    allData,
    readOnly,
    elementSelector,
    actions,
    actionCallbacks,
    onDataChanged,
    customActions,
    onRow,
    rowsAreSelectable = true,
    isFetching,
    name,
    readOnlyVisibilityEnabled,
    resourceUrl,
    pagination,
    addButtonName = "addButton",
    onSelectionChange,
}: PropertyTableProps<T>) => {
    const { t } = useTranslation();

    const [selectedElement, setSelectedElement] = useState<T | null>(null);
    const [selectedRowKeys, setSelectedRowKeys] = useState<Key[]>([]);
    const [selectedRows, setSelectedRows] = useState<T[]>([]);
    const [hasDuplicates, setHasDuplicates] = useState(false);
    const [sortInfo, setSortInfo] = useState<SorterResult<T>>({});

    const baseURI = `data-groups/${getAssignedDataGroup()?.code}`;
    const resourceId = selectedRows?.length ? selectedRows[0].id : "";

    const dispatch = useDispatch();
    const navigate = useNavigate();

    const addRow = () => {
        if (selectedElement === null) return;

        if (onDataChanged) {
            const baseData = allData ?? tableData;
            const exists = baseData.some((x) => x[propertyAsRowKey] === selectedElement[propertyAsRowKey]);
            if (!exists) {
                const newTableData = [...baseData, selectedElement];
                onDataChanged(newTableData);
                setSelectedElement(null);
                setHasDuplicates(false);
            } else {
                setHasDuplicates(true);
            }
        }
    };

    const deleteRow = () => {
        if (onDataChanged) {
            const newValue = tableData.filter((element) => {
                const key = element[propertyAsRowKey as keyof Entity];
                return typeof key !== "boolean" && key !== undefined && !selectedRowKeys.includes(key);
            });
            setSelectedRowKeys([]);
            onDataChanged(newValue);
        }
    };

    const columns = useMemo(() => getColumnsConfig(t, sortInfo).filter((item) => !item.hidden), [t, sortInfo, getColumnsConfig]);

    const sortedTableData = useMemo(() => sortTableData(tableData, sortInfo, columns), [tableData, sortInfo, columns]);

    const onTableChange: TableProps<T>["onChange"] = (_pagination, _filters, sorter) => {
        setSortInfo(Array.isArray(sorter) ? (sorter[0] ?? {}) : sorter);
    };

    const onSelectChange = (newSelectedRowKeys: Key[], rows: T[]) => {
        setSelectedRows(rows);
        setSelectedRowKeys(newSelectedRowKeys);
        onSelectionChange?.(newSelectedRowKeys);
    };

    const rowSelection = {
        selectedRowKeys,
        onChange: onSelectChange,
    };

    const canAdd = actions?.some((action) => action === Actions.ADD);
    const canDelete = actions?.some((action) => action === Actions.DELETE);
    const canCopy = actions?.some((action) => action === Actions.COPY);
    const canFormCopy = actions?.some((action) => action === Actions.FORM_COPY);

    const notifyOkCopy = useCallback(
        (payload: ExtendedAlertActionPayload) => {
            dispatch(
                showSuccessNotification({
                    text: i18n.t("common.formCopySuccess", {
                        id: payload.id,
                        resource: payload.name,
                    }),
                    autoCloseDisabled: false,
                }),
            );
        },
        [dispatch],
    );

    const [updateTable, { isLoading: isUpdating }] = useUpdateTableDataMutation();

    const backendCopy = useCallback(() => {
        updateTable({
            url: `${baseURI}/${resourceUrl}/${resourceId}/${FORM_ACTION.COPY}`,
            method: "POST",
        })
            .unwrap()
            .then((payload) => performCopySuccess(payload))
            .catch(() => {
                // error handled in rtkQueryErrorLogger
            });

        const performCopySuccess = (payload: AlertActionPayload | undefined) => {
            setSelectedRows([]);
            const payloadIdObject = payload as Entity;
            navigate(`${BASE_PATH}/${resourceUrl}/${FORM_ACTION.EDIT}/${payloadIdObject.id}`);
            if (payload) {
                notifyOkCopy(payload);
            }
        };
    }, [updateTable, baseURI, resourceUrl, resourceId, navigate, notifyOkCopy]);

    const formCopy = useCallback(() => {
        navigate(`${BASE_PATH}/${resourceUrl}/${FORM_ACTION.COPY}/${resourceId}`);
    }, [navigate, resourceId, resourceUrl]);

    return (
        <>
            {elementSelector &&
                React.cloneElement(elementSelector, {
                    onSelected: setSelectedElement,
                })}
            <ActionBar className="left">
                {canAdd && (
                    <SecondaryButton
                        onClick={actionCallbacks?.add || addRow}
                        disabled={elementSelector !== undefined && !selectedElement}
                        name={addButtonName}
                    >
                        {i18n.t("button.add")}
                    </SecondaryButton>
                )}
                {canDelete && (
                    <DeleteButton
                        callback={actionCallbacks?.delete || deleteRow}
                        isDisabled={selectedRowKeys.length !== 1}
                        itemToDelete={selectedRows[0]}
                    />
                )}
                {canCopy && (
                    <CopyButton
                        isDisabled={selectedRowKeys.length !== 1}
                        itemToCopy={selectedRows[0]}
                        callback={actionCallbacks?.copy || backendCopy}
                    />
                )}
                {canFormCopy && (
                    <CopyButton
                        isDisabled={selectedRowKeys.length !== 1}
                        itemToCopy={selectedRows[0]}
                        callback={actionCallbacks?.formCopy || formCopy}
                    />
                )}
                {customActions?.map((action, index) =>
                    React.cloneElement(action, {
                        key: action.key ?? index,
                        item: selectedRows.length > 0 ? selectedRows[0] : undefined,
                        items: selectedRows,
                    }),
                )}
            </ActionBar>

            {hasDuplicates && (
                <NotificationAlert description="common.input.duplicatedEntry.error" type={NotificationType.ERROR} isCloseable={false} />
            )}
            <TableStyle>
                <Table<T>
                    id={name}
                    loading={isFetching || isUpdating}
                    rowSelection={rowsAreSelectable ? rowSelection : undefined}
                    columns={columns as ColumnsType<T>}
                    dataSource={addKeyProp<T>(sortedTableData as Readonly<T[]>, propertyAsRowKey)}
                    onRow={onRow}
                    onChange={onTableChange}
                    pagination={pagination ?? { placement: ["bottomCenter"], showSizeChanger: true }}
                    rowClassName={(record) => {
                        if (readOnlyVisibilityEnabled && (readOnly || record.readOnly)) {
                            return "aevi-read-only-row";
                        }
                        return onRow ? "aevi-table-row--clickable" : "";
                    }}
                />
            </TableStyle>
        </>
    );
};

// antd table requires to have a key prop in the table's row object, otherwise
// it will log warning in the console
function addKeyProp<T>(array: ReadonlyArray<T>, propertyAsRowKey: keyof T) {
    return array?.map((obj) => {
        return { ...obj, key: obj[propertyAsRowKey] } as DataItemWithKey<T>;
    });
}

// PropertyTable's data is supplied client-side (not paginated/sorted server-side like GenericTable),
// so a sortable column only needs `sorter: true` (to show sort UI) — PropertyTable does the actual
// reordering here, keyed off the sorted column's dataIndex.
function sortTableData<T>(data: T[], sortInfo: SorterResult<T>, columns: ExtendedColumnsType<T>[]): T[] {
    if (!sortInfo.order || !sortInfo.columnKey) return data;

    const column = columns.find((col) => col.key === sortInfo.columnKey);
    const dataIndex = (column?.dataIndex as keyof T | undefined) ?? (sortInfo.columnKey as keyof T);
    const direction = sortInfo.order === "ascend" ? 1 : -1;

    return [...data].sort((a, b) => compareValues(a[dataIndex], b[dataIndex]) * direction);
}

function compareValues(a: unknown, b: unknown): number {
    if (a == null && b == null) return 0;
    if (a == null) return -1;
    if (b == null) return 1;
    if (typeof a === "number" && typeof b === "number") return a - b;
    return String(a).localeCompare(String(b));
}
